import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Paper, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { useEffect, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { useNavigate } from 'react-router-dom';
import { userModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { useLocation } from 'react-router-dom';
import { useToaster } from 'src/utils/toaster/toasterContext';
import PaymentDetailsTable from './PaymentDetailsTable';
import ReferForm from './ReferForm';
import UserInformationTable from './UserInformationTable';
import { useTranslation } from 'react-i18next';

const ReferAndEarnComponent = () => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [isReferAndEarnEnabled, setIsReferAndEarnEnabled] = useState(true);
  const [loader, setLoader] = useState(false);
  const [referralData, setReferralData] = useState([]);
  const [affiliateLink, setAffiliateLink] = useState();
  const [allTimeCommission, setAllTimeCommission] = useState();
  const [allTimeUsers, setAllTimeUsers] = useState();
  const [referralAPIData, setReferralAPIData] = useState({});
  const [filterDateRange, setFilterDateRange] = useState({
    fromDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    toDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
  });
  const { showToast } = useToaster();

useEffect(() => {
  if (location.state === 'reload') {
    navigate(location.pathname, { replace: true }); // Clear the state first
    window.location.reload(); // Full application reload
  }
  getReferAndEarnData();
}, []);



  const getReferAndEarnData = async () => {
    setLoader(true);
    let postProps = {
      url: userModuleURL.getReferAndEarn,
      isNeedActiveBrandId: false,
      successCallback,
      failureCallback,
    };
    HttpServices.Get(postProps);
  };

  const successCallback = (data, message) => {
    setIsReferAndEarnEnabled(data?.is_refer_and_earn_enabled);
    setReferralData(data?.referral_data || []);
    setReferralAPIData(data || {});
    setAffiliateLink(data?.referral_code_data?.base_url + data?.referral_code_data?.referral_user_code);
    const allTimeCommission =
      data?.referral_data?.payment_data?.reduce((sum, payment) => sum + (payment?.commission_amount ?? 0), 0) ?? 0;
    setAllTimeCommission(allTimeCommission);
    const allTimeUsers = data?.referral_data?.user_creation_data?.length ?? 0;
    setAllTimeUsers(allTimeUsers);

    setLoader(false);
    showToast(message, 's');
  };

  const failureCallback = (message) => {
    setLoader(false);
    showToast(message, 'e');
  };

  const handleFilterDateChange = (e) => {
    const { name, value } = e.target;
    setFilterDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const isDateRangeValid = () => {
    const { fromDate, toDate } = filterDateRange;
    return !fromDate || !toDate || new Date(fromDate) <= new Date(toDate);
  };

  const getDateClass = (date) => {
    const { fromDate, toDate } = filterDateRange;
    const currentDate = new Date(date);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (from && to && currentDate >= from && currentDate <= to) {
      return { backgroundColor: '#e0f7fa' };
    }
    return {};
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(affiliateLink);
    showToast('Affiliate link copied to clipboard', 's');
  };

  if (loader) {
    return <Spinner />;
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h4">{translate('navbar.menu.refer_and_earn')}</Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          justifyContent="center"
          flexGrow={1}
          sx={{ padding: '4px', borderRadius: '4px' }}
        >
          <Typography variant="body1" sx={{ color: theme.palette.primary.dark }}>
            {translate('refer_and_earn.affiliate_link')}: {affiliateLink}
          </Typography>
          <Tooltip title={translate('common.copy_to_clipboard', 'Copy to clipboard')}>
            <IconButton onClick={copyToClipboard} sx={{ color: theme.palette.primary.main, borderRadius: '4px' }}>
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <TextField
            type="date"
            name="fromDate"
            label={translate('refer_and_earn.from_date')}
            value={filterDateRange.fromDate}
            onChange={handleFilterDateChange}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            sx={getDateClass(filterDateRange.fromDate)}
          />
          <TextField
            type="date"
            name="toDate"
            label={translate('refer_and_earn.to_date')}
            value={filterDateRange.toDate}
            onChange={handleFilterDateChange}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: filterDateRange.fromDate,
            }}
            sx={getDateClass(filterDateRange.toDate)}
          />
        </div>
      </Box>
      {isReferAndEarnEnabled ? (
        <Box component={Paper} padding={2} marginTop={2}>
          {!isDateRangeValid() && (
            <Typography color="error">{translate('refer_and_earn.from_date_should_be_less_than_to_date')}</Typography>
          )}
          {isDateRangeValid() && (
            <>
              <Box display="flex" justifyContent="space-between" marginBottom={2}>
                <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', padding: 2, width: '23%' }}>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                    {translate('refer_and_earn.all_unique_users')}
                  </Typography>
                  <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                    {referralAPIData?.cookie_data?.total_cookies}
                  </Typography>
                </Box>
                <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', padding: 2, width: '23%' }}>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                    {translate('refer_and_earn.all_time_account_created')}
                  </Typography>
                  <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                    {allTimeUsers}
                  </Typography>
                </Box>
                <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', padding: 2, width: '23%' }}>
                  <Typography variant="h6" sx={{ color: theme.palette.primary.main }}>
                    {translate('refer_and_earn.all_time_commission_earned')}
                  </Typography>
                  <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                    {allTimeCommission}$
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', padding: 2, marginBottom: 2 }}>
                <UserInformationTable
                  userCreationData={referralData.user_creation_data}
                  filterDateRange={filterDateRange}
                />
              </Box>
              <Box sx={{ border: '1px solid #ccc', borderRadius: '8px', padding: 2 }}>
                <PaymentDetailsTable PaymentData={referralData.payment_data} filterDateRange={filterDateRange} />
              </Box>
            </>
          )}
        </Box>
      ) : (
        <ReferForm />
      )}
    </>
  );
};

export default ReferAndEarnComponent;
